import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  X,
  Search,
  Users,
  Loader2,
  CheckSquare,
  Square,
  UserPlus,
  UserMinus,
  RefreshCcw
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { createPhotoDataUrl, hasPhoto } from '../lib/photoUtils'

interface RawStudent {
  id: string
  first_name: string
  last_name: string
  klasse: string
  email?: string
  photo_url?: string | null
  is_in_course?: number | boolean
  is_blocked?: number | boolean
  enrollment_note?: string | null
}

type Student = Omit<RawStudent, 'is_in_course' | 'is_blocked'> & {
  is_in_course: boolean
  is_blocked: boolean
  enrollment_note: string | null
}

interface StudentsResponse {
  success: boolean
  data: RawStudent[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

interface StudentManagementModalProps {
  courseId?: number
  courseName?: string
  unitType?: 'class' | 'course' | 'group'
  unitId?: number
  unitName?: string
  onClose: () => void
  onStudentsUpdated: () => void
}

const SEARCH_DEBOUNCE_MS = 250
const PARTICIPANTS_OPTION_VALUE = '__participants__'

export default function StudentManagementModal({
  courseId,
  courseName,
  unitType,
  unitId,
  unitName,
  onClose,
  onStudentsUpdated
}: StudentManagementModalProps) {
  // Unterstützung für alte API (courseId) und neue API (unitType/unitId)
  const effectiveUnitType = unitType || 'course'
  const effectiveUnitId = unitId || courseId
  const effectiveUnitName = unitName || courseName || ''
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isMutating, setIsMutating] = useState(false)

  // Debounce search input to limit API calls
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [searchTerm])

  const classesQuery = useQuery(['courses', 'students', 'classes'], async () => {
    const response = await api.get('/courses-groups/students/classes')
    return (response.data?.data ?? []) as string[]
  })

  const courseClassLinksQuery = useQuery(
    ['course-class-links', effectiveUnitId],
    async () => {
      const response = await api.get(`/memberships/units/course/${effectiveUnitId}/class-links`)
      return (response.data?.data ?? []) as Array<{ class_name: string }>
    },
    {
      enabled: effectiveUnitType === 'course' && Boolean(effectiveUnitId)
    }
  )

  const studentsQuery = useQuery<StudentsResponse>(
    ['courses', 'students', effectiveUnitType, effectiveUnitId, debouncedSearch, selectedClass],
    async () => {
      const response = await api.get('/courses-groups/students', {
        params: {
          unitType: effectiveUnitType,
          unitId: effectiveUnitId,
          courseId: effectiveUnitType !== 'class' ? effectiveUnitId : undefined,
          limit: 2000,
          offset: 0,
          search: debouncedSearch || undefined,
          klasse: selectedClass && selectedClass !== PARTICIPANTS_OPTION_VALUE ? selectedClass : undefined,
          onlyAvailable: selectedClass === PARTICIPANTS_OPTION_VALUE ? 'false' : undefined
        }
      })
      return response.data
    },
    {
      keepPreviousData: true,
      onError: () => {
        toast.error('Schüler konnten nicht geladen werden')
      }
    }
  )

  const students = useMemo<Student[]>(() => {
    const list = (studentsQuery.data?.data ?? []).map(student => ({
      ...student,
      is_in_course: Boolean(student.is_in_course),
      is_blocked: Boolean(student.is_blocked),
      enrollment_note: student.enrollment_note || null
    }))
    return list.sort((a, b) => {
      const rank = (s: Student) => (s.is_in_course ? 2 : s.is_blocked ? 1 : 0)
      return rank(b) - rank(a)
    })
  }, [studentsQuery.data])

  // Ensure we drop selections that no longer exist after filtering
  useEffect(() => {
    if (students.length === 0) {
      setSelectedIds([])
      return
    }

    setSelectedIds(prev => prev.filter(id => students.some(student => student.id === id)))
  }, [students])

  const selectedStudents = useMemo<Student[]>(() => {
    const byId = new Map(students.map(student => [student.id, student]))
    return selectedIds
      .map(id => byId.get(id))
      .filter((student): student is Student => Boolean(student))
  }, [students, selectedIds])

  const selectedForAdd = selectedStudents.filter(student => !student.is_in_course && !student.is_blocked)
  const selectedForRemoval = selectedStudents.filter(student => student.is_in_course)

  const totalStudents = studentsQuery.data?.pagination?.total ?? students.length

  const toggleSelection = useCallback((studentId: string) => {
    const student = students.find(s => s.id === studentId)
    if (student?.is_blocked && !student.is_in_course) {
      toast.error(student.enrollment_note || 'Schüler ist parallel in einem anderen Kurs eingebunden')
      return
    }
    setSelectedIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    )
  }, [students])

  const toggleSelectAll = useCallback(() => {
    if (students.length === 0) {
      return
    }

    const visibleIds = students.map(student => student.id)
    const allSelected = visibleIds.every(id => selectedIds.includes(id))

    setSelectedIds(allSelected ? [] : visibleIds)
  }, [students, selectedIds])

  const handleAddStudents = useCallback(async () => {
    if (selectedForAdd.length === 0) {
      return
    }

    try {
      setIsMutating(true)
      const studentIds = selectedForAdd.map(student => student.id)

      // Verwende neues Membership-System
      const today = new Date().toISOString().split('T')[0]
      const membershipPromises = studentIds.map(studentId =>
        api.post('/memberships', {
          student_id: studentId,
          unit_type: effectiveUnitType,
          unit_id: effectiveUnitId,
          valid_from: today,
          valid_until: null,
          notes: null
        })
      )

      await Promise.all(membershipPromises)
      toast.success(`${studentIds.length} Schüler hinzugefügt`)

      setSelectedIds(prev => prev.filter(id => !studentIds.includes(id)))
      await studentsQuery.refetch()
      queryClient.invalidateQueries('courses')
      queryClient.invalidateQueries(['units', effectiveUnitType])
      onStudentsUpdated()
    } catch (error: any) {
      console.error('Fehler beim Hinzufügen von Schülern', error)
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Schüler konnten nicht hinzugefügt werden')
    } finally {
      setIsMutating(false)
    }
  }, [effectiveUnitType, effectiveUnitId, selectedForAdd, studentsQuery, queryClient, onStudentsUpdated])

  const handleRemoveStudents = useCallback(async () => {
    if (selectedForRemoval.length === 0) {
      return
    }

    try {
      setIsMutating(true)
      const studentIds = selectedForRemoval.map(student => student.id)

      // Hole aktive Memberships für diese Schüler und Unit
      const membershipPromises = studentIds.map(async (studentId) => {
        // Hole aktive Membership
        const membershipsResponse = await api.get('/memberships', {
          params: {
            student_id: parseInt(studentId),
            unit_type: effectiveUnitType,
            unit_id: effectiveUnitId,
            status: 'active'
          }
        })
        
        const memberships = membershipsResponse.data?.data || []
        // Beende alle aktiven Memberships
        return Promise.all(
          memberships.map((membership: any) =>
            api.post(`/memberships/${membership.id}/end`, {
              reason: 'Entfernt durch Benutzer'
            })
          )
        )
      })

      await Promise.all(membershipPromises)
      toast.success(`${studentIds.length} Schüler entfernt`)
      setSelectedIds(prev => prev.filter(id => !studentIds.includes(id)))
      await studentsQuery.refetch()
      queryClient.invalidateQueries('courses')
      queryClient.invalidateQueries(['units', effectiveUnitType])
      onStudentsUpdated()
    } catch (error: any) {
      console.error('Fehler beim Entfernen von Schülern', error)
      toast.error(error.response?.data?.message ?? 'Schüler konnten nicht entfernt werden')
    } finally {
      setIsMutating(false)
    }
  }, [effectiveUnitType, effectiveUnitId, selectedForRemoval, studentsQuery, queryClient, onStudentsUpdated])

  const getStudentPhoto = useCallback(
    (student: Student) => {
      const photo = student.photo_url
      if (photo && hasPhoto(photo)) {
        return photo.startsWith('data:') ? photo : createPhotoDataUrl(photo)
      }

      const initials = `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim()
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials || 'Schüler')}&background=3B82F6&color=ffffff&size=64`
    },
    []
  )

  const isLoading = studentsQuery.isLoading || studentsQuery.isFetching

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex h-full max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-6 py-5">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Schüler verwalten</h2>
            <p className="text-sm text-gray-500">
              {effectiveUnitType === 'class' ? 'Klasse' : effectiveUnitType === 'course' ? 'Kurs' : 'Gruppe'}: {effectiveUnitName}
            </p>
            <p className="text-sm text-gray-500">
              {totalStudents} Schüler gefunden · {selectedIds.length} ausgewählt
              {(effectiveUnitType === 'class' || effectiveUnitType === 'course' || effectiveUnitType === 'group') && (
                <span className="ml-2">
                  · <span className="text-emerald-600 font-medium">
                    {students.filter(s => s.is_in_course).length} in dieser {effectiveUnitType === 'class' ? 'Klasse' : effectiveUnitType === 'course' ? 'Kurs' : 'Gruppe'}
                  </span>
                  {' · '}
                  <span className="text-blue-600 font-medium">
                    {students.filter(s => !s.is_in_course && !s.is_blocked).length} können hinzugefügt werden
                  </span>
                  {students.some(s => s.is_blocked) && (
                    <>
                      {' · '}
                      <span className="text-amber-600 font-medium">
                        {students.filter(s => s.is_blocked).length} parallel gebunden
                      </span>
                    </>
                  )}
                </span>
              )}
            </p>
            {effectiveUnitType === 'course' && (courseClassLinksQuery.data?.length ?? 0) > 0 ? (
              <p className="mt-1 text-xs text-indigo-700">
                Auswahl-Pool aus Klassen:{' '}
                {courseClassLinksQuery.data!.map((link) => link.class_name).join(', ')}
              </p>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Modal schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b bg-gray-50 px-6 py-4">
          <div className="grid gap-4 md:grid-cols-[2fr_1fr_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Schüler suchen (Vorname, Name, Klasse)"
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                type="search"
              />
            </div>

            <div>
              <select
                value={selectedClass}
                onChange={event => setSelectedClass(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="">Alle Schüler</option>
                <option value={PARTICIPANTS_OPTION_VALUE}>
                  {effectiveUnitType === 'class' ? 'Nur in dieser Klasse' : effectiveUnitType === 'group' ? 'Nur in dieser Gruppe' : 'Nur Teilnehmer'}
                </option>
                {classesQuery.data?.map(klasse => (
                  <option key={klasse} value={klasse}>
                    Klasse {klasse}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleSelectAll}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100"
                type="button"
              >
                {students.length > 0 && students.every(student => selectedIds.includes(student.id)) ? (
                  <>
                    <CheckSquare className="mr-2 h-3.5 w-3.5" />
                    Alle abwählen
                  </>
                ) : (
                  <>
                    <Square className="mr-2 h-3.5 w-3.5" />
                    Alle auswählen
                  </>
                )}
              </button>

              <button
                onClick={() => studentsQuery.refetch()}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100"
                type="button"
              >
                <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                Aktualisieren
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Lade Schülerdaten…</span>
            </div>
          ) : students.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-gray-500">
              <Users className="h-10 w-10" />
              <span>Keine Schüler gefunden</span>
            </div>
          ) : (
            <div className="h-full overflow-y-auto px-6 py-4">
              <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {students.map(student => {
                  const isSelected = selectedIds.includes(student.id)
                  const inCourse = Boolean(student.is_in_course)
                  const isBlocked = Boolean(student.is_blocked)
                  const photoSrc = getStudentPhoto(student)

                  return (
                    <li
                      key={student.id}
                      className={`flex gap-3 rounded-xl border p-3 transition-shadow ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 shadow-sm'
                          : isBlocked
                          ? 'border-amber-200 bg-amber-50/60'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSelection(student.id)}
                        disabled={isBlocked && !inCourse}
                        className="flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-full border border-gray-300 bg-white disabled:cursor-not-allowed disabled:opacity-50"
                        aria-pressed={isSelected}
                      >
                        {isSelected ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>

                      <img
                        src={photoSrc}
                        alt={`${student.first_name} ${student.last_name}`.trim() || 'Schüler'}
                        className="h-12 w-12 shrink-0 self-start rounded-full object-cover"
                        onError={event => {
                          const target = event.currentTarget
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim() || 'Schüler'
                          )}&background=3B82F6&color=ffffff&size=64`
                        }}
                      />

                      <div className="min-w-0 flex-1 overflow-hidden">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {student.first_name} {student.last_name}
                        </p>
                        <p className="truncate text-xs text-gray-500">Klasse {student.klasse || '—'}</p>
                        <span
                          className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            inCourse
                              ? 'bg-emerald-100 text-emerald-700'
                              : isBlocked
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {inCourse
                            ? (effectiveUnitType === 'class' ? 'In dieser Klasse' : effectiveUnitType === 'group' ? 'In dieser Gruppe' : 'Im Kurs')
                            : isBlocked
                            ? 'Parallel gebunden'
                            : 'Kann hinzugefügt werden'}
                        </span>
                        {student.enrollment_note ? (
                          <p className="mt-1 text-xs text-amber-800 line-clamp-2" title={student.enrollment_note}>
                            {student.enrollment_note}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t bg-gray-50 px-6 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-gray-600">
              <span>{selectedIds.length} Schüler ausgewählt</span>
              {selectedForAdd.length > 0 && (
                <span className="ml-2 text-primary-600">({selectedForAdd.length} zum Hinzufügen)</span>
              )}
              {selectedForRemoval.length > 0 && (
                <span className="ml-2 text-red-600">({selectedForRemoval.length} zum Entfernen)</span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                disabled={selectedIds.length === 0}
              >
                Auswahl leeren
              </button>

              <button
                type="button"
                onClick={handleRemoveStudents}
                disabled={selectedForRemoval.length === 0 || isMutating}
                className="inline-flex items-center rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <UserMinus className="mr-2 h-4 w-4" />
                Entfernen
              </button>

              <button
                type="button"
                onClick={handleAddStudents}
                disabled={selectedForAdd.length === 0 || isMutating}
                className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Hinzufügen
              </button>

              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
